import React, { useEffect, useState } from "react";
import {
  Megaphone,
  Loader2,
  Sparkles,
  Send,
  Facebook,
  Instagram,
  Music2,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Image as ImageIcon,
  Upload,
  Clock,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicImageUrl = (imageId) => `${BACKEND}/api/images/public/${imageId}.jpg`;

const fmtErr = (e, f) => e?.response?.data?.detail || e?.message || f || "Something went wrong.";

const CHANNELS = [
  { id: "facebook", label: "Facebook", Icon: Facebook },
  { id: "instagram", label: "Instagram", Icon: Instagram },
  { id: "tiktok", label: "TikTok", Icon: Music2 },
];

const AdvertisersTab = () => {
  const [loading, setLoading] = useState(true);
  const [advertisers, setAdvertisers] = useState([]);
  const [channelStatus, setChannelStatus] = useState({});
  const [selected, setSelected] = useState(null);
  const [suggestion, setSuggestion] = useState("");
  const [count, setCount] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [posts, setPosts] = useState([]);
  const [chosenChannels, setChosenChannels] = useState([]);
  const [publishingIdx, setPublishingIdx] = useState(null);
  const [pickerIdx, setPickerIdx] = useState(null);

  const patchPost = (idx, patch) =>
    setPosts((ps) => ps.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  useEffect(() => {
    (async () => {
      try {
        const [advRes, metaRes, ayrRes] = await Promise.all([
          api.get("/advertisers/list"),
          api.get("/connections/meta/status").catch(() => ({ data: {} })),
          api.get("/ayrshare/status").catch(() => ({ data: { platforms: [] } })),
        ]);
        setAdvertisers(advRes.data.advertisers || []);
        const linked = ayrRes.data.platforms || [];
        const cs = {
          facebook: !!metaRes.data.facebook_connected, // native Meta
          instagram: !!metaRes.data.instagram_connected, // native Meta
          tiktok: linked.includes("tiktok"), // Ayrshare
        };
        setChannelStatus(cs);
        setChosenChannels(["facebook", "instagram", "tiktok"].filter((id) => cs[id]));
      } catch (e) {
        toast.error(fmtErr(e, "Couldn't load advertisers from paisley.org.uk."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleChannel = (id) => {
    setChosenChannels((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  };

  const generate = async () => {
    if (!selected) return toast.error("Pick an advertiser first.");
    setGenerating(true);
    setPosts([]);
    try {
      const { data } = await api.post("/advertisers/generate", {
        name: selected.name,
        link: selected.link,
        suggestion,
        count: Number(count),
      });
      setPosts(
        (data.posts || []).map((text) => ({
          text,
          image_url: selected?.logo || "",
          image_label: "Advertiser logo",
          mode: "now",
          scheduleAt: "",
        }))
      );
    } catch (e) {
      toast.error(fmtErr(e, "Generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (idx) => {
    if (!chosenChannels.length) return toast.error("Pick at least one channel.");
    const post = posts[idx];
    let schedule_date = null;
    if (post.mode === "schedule") {
      if (!post.scheduleAt) return toast.error("Pick a day & time to schedule.");
      const when = new Date(post.scheduleAt);
      if (when.getTime() <= Date.now()) return toast.error("Pick a time in the future.");
      schedule_date = when.toISOString();
    }
    const verb = schedule_date ? "Schedule" : "Publish now";
    if (!window.confirm(`${verb} to ${chosenChannels.join(", ")}? This posts to real connected accounts.`)) return;
    setPublishingIdx(idx);
    try {
      const { data } = await api.post("/advertisers/publish", {
        caption: post.text,
        image_url: post.image_url || selected?.logo,
        link: selected?.link || "",
        channels: chosenChannels,
        schedule_date,
      });
      const results = data.results || {};
      patchPost(idx, { results });
      const failed = Object.entries(results).filter(
        ([, r]) => r.status !== "published" && r.status !== "scheduled"
      );
      if (data.ok && !failed.length) {
        toast.success(
          schedule_date
            ? `Scheduled for ${new Date(post.scheduleAt).toLocaleString()} → ${chosenChannels.join(", ")}.`
            : `Posted to ${chosenChannels.join(", ")}.`
        );
      } else {
        failed.forEach(([ch, r]) => toast.error(`${ch}: ${r.error || r.status}`));
      }
    } catch (e) {
      toast.error(fmtErr(e, "Publish failed."));
    } finally {
      setPublishingIdx(null);
    }
  };

  return (
    <div data-testid="admin-advertisers">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
        <Megaphone size={20} className="text-cyan-300" /> Advertisers
      </h2>
      <p className="text-slate-400 text-sm mt-1">
        Your Paisley.org.uk advertisers. Pick one, add a suggestion, generate 1-2 spotlight posts,
        and publish to Facebook, Instagram &amp; TikTok.
      </p>

      {/* Channel readiness */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="advertisers-channel-status">
        {CHANNELS.map(({ id, label, Icon }) => (
          <span
            key={id}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border ${
              channelStatus[id]
                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                : "border-amber-400/40 bg-amber-400/10 text-amber-300"
            }`}
          >
            <Icon size={13} /> {label}: {channelStatus[id] ? "connected" : "not connected"}
          </span>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Publishing runs through Ayrshare (Meta/TikTok-approved) — connected accounts post
        immediately. Link Instagram/TikTok in your Ayrshare dashboard to enable them here.
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-cyan-300" />
        </div>
      ) : (
        <div className="mt-6 grid lg:grid-cols-[320px_1fr] gap-6">
          {/* Advertiser list */}
          <div className="space-y-2 max-h-[600px] overflow-auto pr-1" data-testid="advertisers-list">
            {advertisers.length === 0 && (
              <p className="text-slate-500 text-sm">No advertisers found on the feed.</p>
            )}
            {advertisers.map((a) => (
              <button
                key={a.id || a.name}
                onClick={() => {
                  setSelected(a);
                  setPosts([]);
                }}
                data-testid={`advertiser-${a.id || a.name}`}
                className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors ${
                  selected?.name === a.name
                    ? "border-cyan-400/50 bg-cyan-400/10"
                    : "border-white/10 hover:border-white/20"
                }`}
              >
                {a.logo ? (
                  <img
                    src={a.logo}
                    alt={a.name}
                    className="w-10 h-10 object-contain bg-white rounded-sm shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 bg-white/5 rounded-sm shrink-0" />
                )}
                <span className="text-sm text-white truncate">{a.name}</span>
              </button>
            ))}
          </div>

          {/* Composer */}
          <div>
            {!selected ? (
              <div className="p-8 border border-dashed border-white/10 rounded-sm text-center text-slate-500">
                Select an advertiser to start.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-[#0A192F] border border-white/10 rounded-sm">
                  {selected.logo && (
                    <img src={selected.logo} alt="" className="w-12 h-12 object-contain bg-white rounded-sm" />
                  )}
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-white">{selected.name}</p>
                    {selected.link && (
                      <a
                        href={selected.link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 truncate"
                      >
                        {selected.link.replace(/^https?:\/\//, "").slice(0, 40)} <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>

                <div className="p-4 bg-[#0A192F] border border-white/10 rounded-sm space-y-3">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
                    Your suggestion / angle (optional)
                  </label>
                  <textarea
                    value={suggestion}
                    onChange={(e) => setSuggestion(e.target.value)}
                    rows={2}
                    placeholder="e.g. mention their new autumn class timetable"
                    data-testid="advertisers-suggestion"
                    className="w-full px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-sm rounded-sm resize-none"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Posts:</span>
                      {[1, 2].map((n) => (
                        <button
                          key={n}
                          onClick={() => setCount(n)}
                          data-testid={`advertisers-count-${n}`}
                          className={`px-3 py-1.5 rounded-sm text-xs border ${
                            count === n
                              ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"
                              : "border-white/10 text-slate-400"
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={generate}
                      disabled={generating}
                      data-testid="advertisers-generate-btn"
                      className="ml-auto inline-flex items-center gap-2 px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-widest hover:bg-[#33F3FF] disabled:opacity-50"
                    >
                      {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{" "}
                      Generate
                    </button>
                  </div>
                </div>

                {/* Channel picker */}
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map(({ id, label, Icon }) => {
                    const connected = channelStatus[id];
                    return (
                      <button
                        key={id}
                        onClick={() => connected && toggleChannel(id)}
                        disabled={!connected}
                        title={connected ? "" : `${label} not connected`}
                        data-testid={`advertisers-channel-${id}`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs border ${
                          chosenChannels.includes(id)
                            ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"
                            : "border-white/10 text-slate-400"
                        } ${!connected ? "opacity-40 cursor-not-allowed" : ""}`}
                      >
                        <Icon size={13} /> {label}
                      </button>
                    );
                  })}
                </div>

                {/* Generated posts */}
                {posts.map((p, i) => (
                  <div
                    key={i}
                    data-testid={`advertisers-post-${i}`}
                    className="p-4 bg-[#0A192F] border border-cyan-400/20 rounded-sm space-y-3"
                  >
                    <p className="text-sm text-slate-200 whitespace-pre-wrap">{p.text}</p>

                    {/* Image row */}
                    <div className="flex items-center gap-3">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt=""
                          className="w-16 h-16 object-contain bg-white rounded-sm border border-white/10"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-white/5 rounded-sm flex items-center justify-center text-slate-600">
                          <ImageIcon size={20} />
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-400">
                          Image: <span className="text-slate-200">{p.image_label || "custom"}</span>
                        </p>
                        <button
                          onClick={() => setPickerIdx(i)}
                          data-testid={`advertisers-change-image-${i}`}
                          className="mt-1 inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-100"
                        >
                          <ImageIcon size={12} /> Change image
                        </button>
                      </div>
                    </div>

                    {/* Schedule row */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => patchPost(i, { mode: "now" })}
                        data-testid={`advertisers-mode-now-${i}`}
                        className={`px-3 py-1.5 rounded-sm text-xs border ${
                          p.mode === "now"
                            ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"
                            : "border-white/10 text-slate-400"
                        }`}
                      >
                        Post now
                      </button>
                      <button
                        onClick={() => patchPost(i, { mode: "schedule" })}
                        data-testid={`advertisers-mode-schedule-${i}`}
                        className={`px-3 py-1.5 rounded-sm text-xs border inline-flex items-center gap-1 ${
                          p.mode === "schedule"
                            ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200"
                            : "border-white/10 text-slate-400"
                        }`}
                      >
                        <Clock size={12} /> Schedule
                      </button>
                      {p.mode === "schedule" && (
                        <input
                          type="datetime-local"
                          value={p.scheduleAt}
                          onChange={(e) => patchPost(i, { scheduleAt: e.target.value })}
                          data-testid={`advertisers-schedule-input-${i}`}
                          className="px-3 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm"
                        />
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-white/5">
                      <div className="flex flex-wrap gap-2 text-[11px]">
                        {p.results &&
                          Object.entries(p.results).map(([ch, v]) => (
                            <span
                              key={ch}
                              className={`inline-flex items-center gap-1 ${
                                v.status === "failed" ? "text-amber-300" : "text-emerald-300"
                              }`}
                            >
                              {v.status === "failed" ? (
                                <AlertTriangle size={12} />
                              ) : (
                                <CheckCircle2 size={12} />
                              )}
                              {ch}
                              {v.status === "scheduled" ? " (scheduled)" : ""}
                            </span>
                          ))}
                      </div>
                      <button
                        onClick={() => publish(i)}
                        disabled={publishingIdx === i}
                        data-testid={`advertisers-publish-${i}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm text-xs uppercase tracking-wider hover:bg-[#33F3FF] disabled:opacity-50"
                      >
                        {publishingIdx === i ? (
                          <Loader2 size={13} className="animate-spin" />
                        ) : p.mode === "schedule" ? (
                          <Clock size={13} />
                        ) : (
                          <Send size={13} />
                        )}{" "}
                        {p.mode === "schedule" ? "Schedule" : "Publish now"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {pickerIdx !== null && (
        <ImagePicker
          onClose={() => setPickerIdx(null)}
          onPick={(url, label) => {
            patchPost(pickerIdx, { image_url: url, image_label: label });
            setPickerIdx(null);
          }}
        />
      )}
    </div>
  );
};

const ImagePicker = ({ onClose, onPick }) => {
  const [tab, setTab] = useState("library");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/images/list");
      setImages(data || []);
    } catch (e) {
      toast.error(fmtErr(e, "Couldn't load your library."));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Added to your library.");
      onPick(publicImageUrl(data.image_id), file.name || "Uploaded image");
    } catch (e) {
      toast.error(fmtErr(e, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="advertisers-image-picker">
      <div className="w-full max-w-2xl bg-[#0A192F] border border-cyan-400/30 rounded-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-heading font-bold text-white text-sm flex items-center gap-2">
            <ImageIcon size={15} className="text-cyan-300" /> Choose an image
          </h3>
          <button onClick={onClose} data-testid="image-picker-close" className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 px-5 pt-4">
          <button
            onClick={() => setTab("library")}
            data-testid="image-picker-tab-library"
            className={`px-3 py-1.5 rounded-sm text-xs border ${tab === "library" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-slate-400"}`}
          >
            Library
          </button>
          <label
            data-testid="image-picker-tab-upload"
            className="px-3 py-1.5 rounded-sm text-xs border border-white/10 text-slate-300 hover:border-cyan-400/40 cursor-pointer inline-flex items-center gap-1"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload new
            <input
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="image-picker-upload-input"
              onChange={(e) => upload(e.target.files?.[0])}
            />
          </label>
        </div>
        <div className="p-5 max-h-[420px] overflow-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-cyan-300" /></div>
          ) : images.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">
              Your library is empty — upload an image to get started.
            </p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((img) => (
                <button
                  key={img.image_id}
                  onClick={() => onPick(publicImageUrl(img.image_id), img.prompt?.slice(0, 24) || "Library image")}
                  data-testid={`image-picker-img-${img.image_id}`}
                  className="aspect-square bg-[#050B14] border border-white/10 hover:border-cyan-400/60 rounded-sm overflow-hidden"
                >
                  {img.data_url && <img src={img.data_url} alt="" className="w-full h-full object-cover" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvertisersTab;
